import { getMixpanelDistinctID } from '@/analytics/mixpanel/helpers'
import { platform } from '@/services/platform'
import { assertNotNull } from '@/helpers/typing'
import { Sentry } from '@/services/sentry'

import { storage } from './storage'
import {
  ABVariantData,
  ABSideloadedResponse,
  CheckResponseHint,
  Experiment,
  StartEvent,
} from './types'

export async function createStartEvent(
  experiment: Experiment,
  variant: string | null,
  includeMixpanelDeviceId: boolean,
): Promise<StartEvent> {
  const info = await platform.deviceInfo()
  const data: StartEvent = {
    client_id: storage.clientId(),
    experiment: experiment,
    url: window.location.href,
    referrer: window.document.referrer,
    platform: info.platform,
    os: info.osVersion,
    variant: variant,
  }

  if (includeMixpanelDeviceId) {
    data.mixpanel_device_id = getMixpanelDistinctID()
  }

  return data
}

export function persistStartedVariant(
  experiment: Experiment,
  variantData: ABVariantData,
  clientId: string | null,
): void {
  if (!clientId) {
    storage.setClientId(variantData.client_id)
  }
  storage.setExperiment(experiment, variantData.variant)
}

export function checkResponse(
  responseData: ABSideloadedResponse,
  hint: CheckResponseHint,
): boolean {
  if (!responseData.ab) {
    Sentry.withScope((scope) => {
      scope.setExtra('responseData', responseData)
      scope.setExtra('hint', hint)
      Sentry.captureMessage('No A/B data found in response')
    })
    return false
  }
  const abData = responseData.ab
  const client_id = storage.clientId()

  if (
    client_id &&
    client_id !== 'undefined' &&
    client_id !== abData.client_id
  ) {
    if (hint === CheckResponseHint.register) {
      Sentry.withScope((scope) => {
        scope.setExtra('frontendClientId', storage.clientId())
        scope.setExtra('frontendExperiments', storage.getExperiments())
        scope.setExtra('backendData', abData)
        scope.setFingerprint(['reset-ab-data-on-signup', hint])
        Sentry.captureMessage('Reset A/B client data on signup')
      })
    }
  }
  if (hint === CheckResponseHint.abStartError) {
    Sentry.withScope((scope) => {
      scope.setExtra('frontendClientId', storage.clientId())
      scope.setExtra('frontendExperiments', storage.getExperiments())
      scope.setExtra('backendData', abData)
      scope.setFingerprint(['reset-ab-data-on-ab-start-error', hint])
      Sentry.captureMessage('Reset A/B client data on ab/start error')
    })
  }

  storage.setClientId(assertNotNull(abData.client_id))
  storage.setExperiments(abData.experiments)

  return true
}
